import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Box, Button, Typography } from "@mui/material";
import YouTubeIcon from "@mui/icons-material/YouTube";
import LinkIcon from "@mui/icons-material/Link";
import {
  TUTORIAL_VIDEOS,
  getVideoLanguage,
  youTubeEmbedUrl,
  youTubeWatchUrl,
} from "../../../utilities/tutorialVideos";

// Page copy is local so this page doesn't depend on a backend language-file deploy.
const COPY = {
  en: {
    title: "Video tutorials",
    subtitle: "Short videos that show how to use TeamPlayer.",
    leaderTitle: "For team leaders",
    leaderDescription: "Set up your team, add members and assign tasks.",
    playerTitle: "For your team members",
    playerDescription:
      "Time in and out, complete tasks and submit expenses. Share this video with your team.",
    openInYouTube: "Open in YouTube",
    copyLink: "Copy link",
    copied: "Link copied",
  },
  hi: {
    title: "वीडियो ट्यूटोरियल",
    subtitle: "TeamPlayer इस्तेमाल करना सिखाने वाले छोटे वीडियो।",
    leaderTitle: "टीम लीडर के लिए",
    leaderDescription: "अपनी टीम बनाएं, सदस्य जोड़ें और काम सौंपें।",
    playerTitle: "आपकी टीम के सदस्यों के लिए",
    playerDescription:
      "टाइम इन और आउट करें, काम पूरे करें और खर्च जमा करें। यह वीडियो अपनी टीम के साथ शेयर करें।",
    openInYouTube: "YouTube में खोलें",
    copyLink: "लिंक कॉपी करें",
    copied: "लिंक कॉपी हो गया",
  },
};

// Explicit colours/size: the app's global styles turn MUI icons white and shrink link text.
const ACTION_COLOR = "#256FE8";
const actionSx = { textTransform: "none", fontWeight: 600, fontSize: 14, color: ACTION_COLOR };

const VideoCard = ({ videoId, title, description, copy, shareable }) => {
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(youTubeWatchUrl(videoId));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be blocked; "Open in YouTube" still works.
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        border: "1px solid #E5E7EB",
        borderRadius: "16px",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ position: "relative", width: "100%", paddingTop: "56.25%", backgroundColor: "#000" }}>
        <iframe
          src={youTubeEmbedUrl(videoId)}
          title={title}
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: 0 }}
        />
      </Box>
      <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 600, color: "#282C34" }}>{title}</Typography>
        <Typography sx={{ fontSize: 14, color: "#6B7280", lineHeight: 1.5 }}>{description}</Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: "auto", pt: 1 }}>
          <Button
            size="small"
            variant="text"
            startIcon={<YouTubeIcon sx={{ color: "#FF0000" }} />}
            href={youTubeWatchUrl(videoId)}
            target="_blank"
            rel="noopener noreferrer"
            sx={actionSx}
          >
            {copy.openInYouTube}
          </Button>
          {shareable && (
            <Button
              size="small"
              variant="text"
              startIcon={<LinkIcon sx={{ color: ACTION_COLOR }} />}
              onClick={copyLink}
              sx={actionSx}
            >
              {copied ? copy.copied : copy.copyLink}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const Demo = () => {
  // Re-render when the language is switched (the language modal refetches this).
  useSelector((state) => state.allUsers.language_json);
  const lang = getVideoLanguage();
  const copy = COPY[lang];

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", py: 1 }}>
      <Typography sx={{ fontSize: 24, fontWeight: 700, color: "#282C34" }}>{copy.title}</Typography>
      <Typography sx={{ fontSize: 14, color: "#6B7280", mt: 0.5, mb: 3 }}>{copy.subtitle}</Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 3,
        }}
      >
        <VideoCard
          videoId={TUTORIAL_VIDEOS.team_leader[lang]}
          title={copy.leaderTitle}
          description={copy.leaderDescription}
          copy={copy}
        />
        <VideoCard
          videoId={TUTORIAL_VIDEOS.team_player[lang]}
          title={copy.playerTitle}
          description={copy.playerDescription}
          copy={copy}
          shareable
        />
      </Box>
    </Box>
  );
};

export default Demo;
